import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as InfoIcon } from "assets/images/icons/icn_table_cell_Info.svg";

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ModalContainer from "components/ModalContainer";
import InputField from "components/InputField";
import { OutlineButton } from "components/buttons/OutlineButton";
import { RadioButton } from "components/buttons/RadioButton";
import { SolidButton } from "components/buttons/SolidButton";

import ScheduleTimeField from "./components/ScheduleTimeField";
import { dayOfWeekOptions, defaultScheduleProfile } from "./util/const";
import Form from "./util/form";

const MINUTES_PER_DAY = 24 * 60;
const TIMELINE_SIDE_PADDING = 36;
const scheduleGridColumns = "130px 200px 124px 18px 124px minmax(0, 1fr)";
const tooltipIconStyle = {
  width: "20px",
  height: "20px",
  cursor: "default",
};

const timeToMinutes = (time) => {
  const [hour = 0, minute = 0] = String(time || "00:00").split(":").map(Number);

  return (hour * 60) + minute;
};

const formatDisplayTime = (time, timeFormat) => {
  if (timeFormat === "24h") return time;
  if (time === "24:00") return "12:00 AM";

  const minutes = timeToMinutes(time);
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
};

const getSegments = (day) => {
  if (!day.enabled) return [];

  const from = timeToMinutes(day.from);
  const to = timeToMinutes(day.to);

  if (from === to) return [{ start: 0, end: MINUTES_PER_DAY }];

  if (from < to) return [{ start: from, end: to }];

  return [
    { start: from, end: MINUTES_PER_DAY },
    { start: 0, end: to },
  ];
};

const Timeline = ({ day, timeFormat }) => {
  const theme = useTheme();
  const segments = getSegments(day);

  return (
    <Box sx={{ height: "30px", minWidth: 0, width: "100%", px: `${TIMELINE_SIDE_PADDING}px`, overflow: "hidden" }}>
      <Box sx={{ position: "relative", height: "30px" }}>
        <Box
          sx={{
            position: "absolute",
            top: "14px",
            left: 0,
            right: 0,
            height: "4px",
            backgroundColor: theme.palette.primary.greyThread,
          }}
        />
        {segments.map((segment, index) => {
          const left = (segment.start / MINUTES_PER_DAY) * 100;
          const width = ((segment.end - segment.start) / MINUTES_PER_DAY) * 100;

          return (
            <Box
              key={`${segment.start}-${segment.end}-${index}`}
              sx={{
                position: "absolute",
                top: "14px",
                left: `${left}%`,
                width: `${width}%`,
                height: "4px",
                backgroundColor: theme.palette.primary.greenPrimary,
              }}
            />
          );
        })}
        <Typography
          sx={{
            position: "absolute",
            top: 0,
            left: `${(timeToMinutes(day.from) / MINUTES_PER_DAY) * 100}%`,
            transform: "translateX(-50%)",
            fontSize: "10px",
            lineHeight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatDisplayTime(day.from, timeFormat)}
        </Typography>
        <Typography
          sx={{
            position: "absolute",
            top: "19px",
            left: `${(timeToMinutes(day.to) / MINUTES_PER_DAY) * 100}%`,
            transform: "translateX(-100%)",
            fontSize: "10px",
            lineHeight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatDisplayTime(day.to, timeFormat)}
        </Typography>
      </Box>
    </Box>
  );
};

const ScheduleModal = ({
  open,
  mode,
  profile,
  showEditGeneral = true,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const form = Form.init(theme);
  const isCreateMode = mode === "create";
  const [timeFormat, setTimeFormat] = useState("ampm");
  const [formValue, setFormValue] = useState(structuredClone(defaultScheduleProfile));
  const [isNameTouched, setIsNameTouched] = useState(false);

  const isInvalid = useMemo(() => !formValue.name?.trim(), [formValue.name]);
  const showNameError = isNameTouched && isInvalid;

  const updateDay = (dayOfWeek, patch) => {
    setFormValue((prev) => ({
      ...prev,
      days: prev.days.map((day) => (
        day.dayOfWeek === dayOfWeek
          ? { ...day, ...patch }
          : day
      )),
    }));
  };

  const handleSubmit = () => {
    if (isInvalid) return;
    onSubmit(structuredClone(formValue));
  };

  useEffect(() => {
    if (!open) return;

    if (profile) {
      setFormValue(structuredClone(profile));
    } else {
      setFormValue(structuredClone(defaultScheduleProfile));
    }

    setTimeFormat("ampm");
    setIsNameTouched(false);
  }, [open, profile]);

  return (
    <ModalContainer display={open} modalWidth="1250px">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "26px" }}>
        <Typography
          sx={form.getHeaderTextStyle()}
        >
          {isCreateMode ? "Create" : "Edit"} Schedule Profile
        </Typography>

        <Button
          onClick={onClose}
          sx={{
            minWidth: 0,
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "28px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "110px" }}>
          <Box sx={{ ...form.getLabelStyle("120px"), ml: 3 }}>Schedule Name</Box>
          <Box sx={{ width: "300px" }}>
            <InputField
              value={formValue.name}
              isValid={!showNameError}
              onChange={(value) => {
                setIsNameTouched(true);
                setFormValue((prev) => ({ ...prev, name: value }));
              }}
            />
          </Box>
        </Box>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={timeFormat}
          onChange={(_, value) => value && setTimeFormat(value)}
          sx={{
            width: "200px",
            height: "36px",
            padding: "3px",
            border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.white,
            "& .MuiToggleButton-root": {
              flex: 1,
              height: "28px",
              border: 0,
              borderRadius: "6px",
              color: theme.palette.primary.greyPrimary,
              fontSize: theme.typography.customFontSize[1],
              fontWeight: theme.typography.customFontWeight[0],
              textTransform: "none",
              "&.Mui-selected": {
                color: theme.palette.primary.main,
                backgroundColor: "#DDE2FF",
                fontWeight: theme.typography.customFontWeight[2],
              },
              "&.Mui-selected:hover": {
                backgroundColor: "#DDE2FF",
              },
              "&:hover": {
                backgroundColor: theme.palette.primary.whiteSecound,
              },
              "&:not(:first-of-type)": {
                marginLeft: 0,
              },
            },
          }}
        >
          <ToggleButton value="ampm">AM/PM</ToggleButton>
          <ToggleButton value="24h">24 Hrs</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: scheduleGridColumns,
          alignItems: "center",
          columnGap: "8px",
          borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
          pb: "12px",
        }}
      >
        <Typography sx={{ ...form.getColumnHeaderStyle(), ml: 3 }}>Day of Week</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={form.getColumnHeaderStyle()}>Status</Typography>
        </Box>
        <Typography sx={form.getColumnHeaderStyle()}>From</Typography>
        <Box />
        <Typography sx={form.getColumnHeaderStyle()}>To</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={form.getColumnHeaderStyle()}>Timeline</Typography>
          <Tooltip arrow title="In the timeline view, gray indicates that a feature is disabled, while green indicates that a feature is enabled.">
            <InfoIcon style={tooltipIconStyle} />
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ overflow: "hidden" }}>
        {dayOfWeekOptions.map((option, index) => {
          const day = formValue.days.find((item) => item.dayOfWeek === option.key) || {
            dayOfWeek: option.key,
            enabled: true,
            from: "00:00",
            to: "24:00",
          };

          return (
            <Box
              key={option.key}
              sx={{
                display: "grid",
                gridTemplateColumns: scheduleGridColumns,
                alignItems: "center",
                columnGap: "8px",
                minHeight: "60px",
                px: "0",
                backgroundColor: index % 2 === 1 ? theme.palette.primary.whiteSecound : theme.palette.primary.white,
                color: theme.palette.primary.greyPrimary,
                fontSize: theme.typography.customFontSize[2],
              }}
            >
              <Box sx={{ ...form.getLabelStyle("130px"), ml: 3 }}>{option.label}</Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <RadioButton
                  label="Enable"
                  value={true}
                  checked={day.enabled === true}
                  onChange={() => updateDay(option.key, { enabled: true })}
                  name={`${option.key}-availability`}
                  sx={{ m: 0 }}
                />
                <RadioButton
                  label="Disable"
                  value={false}
                  checked={day.enabled === false}
                  onChange={() => updateDay(option.key, { enabled: false })}
                  name={`${option.key}-availability`}
                  sx={{ m: 0 }}
                />
              </Box>
              <ScheduleTimeField
                value={day.from}
                disabled={!day.enabled}
                timeFormat={timeFormat}
                sx={form.getTextfieldStyle(true, "114px")}
                onChange={(value) => updateDay(option.key, { from: value })}
              />
              <Typography sx={{ textAlign: "center", ml: -1 }}>-</Typography>
              <ScheduleTimeField
                value={day.to}
                disabled={!day.enabled}
                timeFormat={timeFormat}
                allowEndOfDay
                sx={form.getTextfieldStyle(true, "110px")}
                onChange={(value) => updateDay(option.key, { to: value })}
              />
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Timeline day={day} timeFormat={timeFormat} />
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          mt: "30px",
          pt: "24px",
          borderTop: `1px solid ${theme.palette.primary.greyThread}`,
        }}
      >
        <OutlineButton width="100px" height="32px" onClick={onClose}>
          Cancel
        </OutlineButton>
        <SolidButton width="100px" height="32px" disabled={isInvalid || (!isCreateMode && !showEditGeneral)} onClick={handleSubmit}>
          {isCreateMode ? "Create" : "Save"}
        </SolidButton>
      </Box>
    </ModalContainer>
  );
};

export default ScheduleModal;
